import React, { createContext, useContext, useState, useCallback } from "react";
import axiosClient from "../api/axiosClient";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem("vpf_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser());
  const [token, setToken] = useState(localStorage.getItem("vpf_token"));
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const login = useCallback(async (username, password) => {
    // fcm_token is required by the existing LoginAPIView payload; the web
    // dashboard doesn't have push notifications, so we send a stable
    // placeholder value instead of leaving the field out.
    const { data } = await axiosClient.post("/api/v1/login/", {
      username,
      password,
      fcm_token: "web-dashboard",
    });

    if (data?.status?.code !== 200) {
      throw new Error(data?.message || "Invalid username or password");
    }

    const payload = data.data;
    localStorage.setItem("vpf_token", payload.token);
    localStorage.setItem("vpf_refresh", payload.refresh);
    localStorage.setItem("vpf_user", JSON.stringify(payload));

    setToken(payload.token);
    setUser(payload);
    setLoginModalOpen(false);
    return payload;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("vpf_token");
    localStorage.removeItem("vpf_refresh");
    localStorage.removeItem("vpf_user");
    setToken(null);
    setUser(null);
    // Full reload keeps this identical to the session-expiry path and
    // guarantees no stale state lingers anywhere in the app.
    window.location.href = "/";
  }, []);

  const value = {
    user,
    token,
    isAuthenticated: Boolean(token),
    isAdmin: Boolean(user?.isSuper) || user?.role === 0,
    login,
    logout,
    loginModalOpen,
    openLoginModal: () => setLoginModalOpen(true),
    closeLoginModal: () => setLoginModalOpen(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an <AuthProvider>");
  return ctx;
}
